import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ReleaseView {
  readonly releaseId: string;
  readonly releaseCode: string;
  readonly releaseName: string;
  readonly releaseType: string;
  readonly version: string | null;
  readonly description: string | null;
  readonly status: string;
  readonly scheduledStartAt: string | null;
  readonly scheduledEndAt: string | null;
  readonly rollbackPlan: string | null;
  readonly releaseNotes: string | null;
  readonly deployedAt: string | null;
}

interface DeploymentRow {
  readonly deploymentId: string;
  readonly deploymentCode: string;
  readonly environmentType: string;
  readonly status: string;
  readonly completedAt: string | null;
}

/**
 * GR_M31-M35 §L32.03 / §M — release detail viewer (`/admin/devops/releases/:id`).
 *
 * Renders a single release train (T422) with its lifecycle metadata and rollback
 * plan, plus the deployments that carry it to each target environment
 * (§M L32.03 release.target_env → the deployment's environment_type). The
 * artefact URI (§M release.artefact_uri) is a package-scoped field surfaced with
 * the build / deployment records; this view lists the deployment targets so a
 * release manager can see where the release has landed.
 */
export function ReleaseDetailPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [release, setRelease] = useState<ReleaseView | null>(null);
  const [deployments, setDeployments] = useState<DeploymentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m32.releaseDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: ReleaseView }>(`/v1/devops/releases/${id}`, { token })
      .then(async (r) => {
        if (!alive) return;
        setRelease(r.data);
        setError(null);
        try {
          const dep = await apiRequest<{ data: DeploymentRow[] }>(
            `/v1/devops/deployments?releaseId=${id}`, { token });
          if (alive) setDeployments(dep.data ?? []);
        } catch {
          if (alive) setDeployments([]);
        }
      })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m32.releaseDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m32.releaseDetail.authError')
      : (err as Error).message;

  const act = async (path: string, label: string): Promise<void> => {
    if (!release) return;
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/releases/${release.releaseId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m32.releaseDetail.actNotice', { code: release.releaseCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  if (loading) return <main aria-label="release-detail"><p>{t('m32.releaseDetail.loading')}</p></main>;
  if (error) return <main aria-label="release-detail"><p role="alert">{error}</p></main>;
  if (!release) return <main aria-label="release-detail"><p role="alert">{t('m32.releaseDetail.notFound')}</p></main>;

  return (
    <main aria-label="release-detail">
      <h1>{release.releaseName}</h1>
      <dl>
        <dt>{t('m32.releaseDetail.code')}</dt><dd aria-label="release-code">{release.releaseCode}</dd>
        <dt>{t('m32.releaseDetail.type')}</dt><dd aria-label="release-type">{release.releaseType}</dd>
        <dt>{t('m32.releaseDetail.version')}</dt><dd aria-label="release-version">{release.version ?? '—'}</dd>
        <dt>{t('m32.releaseDetail.status')}</dt><dd aria-label="release-status">{release.status}</dd>
        <dt>{t('m32.releaseDetail.scheduledStart')}</dt><dd aria-label="release-window-start">{at(release.scheduledStartAt)}</dd>
        <dt>{t('m32.releaseDetail.rollbackPlan')}</dt><dd aria-label="release-rollback">{release.rollbackPlan ? 'Yes' : 'No'}</dd>
      </dl>

      <section aria-label="release-lifecycle">
        <h2>{t('m32.releaseDetail.lifecycle')}</h2>
        {actionError && <p role="alert" aria-label="release-error">{actionError}</p>}
        {notice && <p aria-label="release-notice">{notice}</p>}
        {release.status === 'PLANNED' && (
          <button type="button" aria-label="package-release" onClick={() => void act('package', t('m32.releaseDetail.act.packaged'))}>{t('m32.releaseDetail.package')}</button>
        )}
        {release.status === 'PACKAGED' && (
          <button type="button" aria-label="schedule-release" onClick={() => void act('schedule', t('m32.releaseDetail.act.scheduled'))}>{t('m32.releaseDetail.schedule')}</button>
        )}
        {release.status === 'SCHEDULED' && (
          <button type="button" aria-label="approve-release" onClick={() => void act('approve', t('m32.releaseDetail.act.approved'))}>{t('m32.releaseDetail.approve')}</button>
        )}
        {release.status === 'APPROVED' && (
          <button type="button" aria-label="deploy-release" onClick={() => void act('deploy', t('m32.releaseDetail.act.deployed'))}>{t('m32.releaseDetail.deploy')}</button>
        )}
        {release.status === 'DEPLOYED' && (
          <button type="button" aria-label="validate-release" onClick={() => void act('validate', t('m32.releaseDetail.act.validated'))}>{t('m32.releaseDetail.validate')}</button>
        )}
        {release.status === 'VALIDATED' && (
          <button type="button" aria-label="close-release" onClick={() => void act('close', t('m32.releaseDetail.act.closed'))}>{t('m32.releaseDetail.close')}</button>
        )}
        {!['CLOSED', 'CANCELLED', 'VALIDATED'].includes(release.status) && (
          <button type="button" aria-label="cancel-release" onClick={() => void act('cancel', t('m32.releaseDetail.act.cancelled'))}>{t('m32.releaseDetail.cancel')}</button>
        )}
      </section>

      {release.releaseNotes && (
        <section aria-label="release-notes">
          <h2>{t('m32.releaseDetail.notesTitle')}</h2>
          <p>{release.releaseNotes}</p>
        </section>
      )}

      <section aria-label="release-deployments">
        <h2>{t('m32.releaseDetail.targetsTitle')}</h2>
        {deployments.length > 0 ? (
          <table aria-label="release-deployment-table">
            <thead><tr><th>{t('m32.releaseDetail.thDeployment')}</th><th>{t('m32.releaseDetail.thTargetEnv')}</th><th>{t('m32.releaseDetail.status')}</th></tr></thead>
            <tbody>
              {deployments.map((d) => (
                <tr key={d.deploymentId} data-status={d.status}>
                  <td>{d.deploymentCode}</td>
                  <td aria-label={`deployment-env-${d.deploymentId}`}>{d.environmentType}</td>
                  <td>{d.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p aria-label="release-no-deployments">{t('m32.releaseDetail.noDeployments')}</p>
        )}
      </section>
    </main>
  );
}
